/**
 * HistorialReservaModal — snapshot histórico de una reserva.
 *
 * Se abre pasándole el `codigo` de reserva. Trae de un solo golpe:
 *   · datos del cliente,
 *   · quién/cuándo creó la reserva,
 *   · snapshot original (según auditoría),
 *   · estado actual,
 *   · timeline cronológico de eventos,
 *   · pagos (con reversiones),
 *   · vouchers (thumbnails clickeables).
 *
 * Diseñado para consulta rápida en operación — auditar cómo se cobró, entender
 * cambios sufridos, verificar comisiones. Es reutilizable desde cualquier
 * pantalla (histórico, pendientes por subsanar, cierres de muelle, etc.).
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Box, Typography, Chip, Divider, CircularProgress, Button, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DevicesIcon from '@mui/icons-material/Devices';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { API_BASE, getAuthHeaders } from './shared/format';

const fmt = (n) => `$${new Intl.NumberFormat('es-CO').format(Number(n || 0))}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit' });
};

// Iconos y colores por tipo de acción de auditoría
const ACCION_META = {
  reserva_creada_admin:  { icon: '🟢', color: '#15803d', label: 'Reserva creada' },
  reserva_creada:        { icon: '🟢', color: '#15803d', label: 'Reserva creada' },
  embarque:              { icon: '🚤', color: '#0891b2', label: 'Embarcada' },
  pago_recibido_creado:  { icon: '💰', color: '#7c3aed', label: 'Pago aplicado' },
  pago_recibido_revertido:{ icon: '↩️', color: '#b91c1c', label: 'Pago revertido' },
  cambio_estado:         { icon: '🔄', color: '#a16207', label: 'Cambio de estado' },
  no_show:               { icon: '❌', color: '#b91c1c', label: 'No-show' },
  reserva_cancelada:     { icon: '🚫', color: '#b91c1c', label: 'Cancelada' },
  edicion_admin:         { icon: '✏️', color: '#a16207', label: 'Editada' },
  voucher_subsanado:     { icon: '✅', color: '#15803d', label: 'Voucher subsanado' },
  cierre_manual:         { icon: '🔒', color: '#7c3aed', label: 'Cierre manual' },
};
const metaAccion = (accion) => ACCION_META[accion] || { icon: '📝', color: '#64748b', label: accion };

export default function HistorialReservaModal({ codigo, open, onClose }) {
  const [voucherLightbox, setVoucherLightbox] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['historial-reserva', codigo],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/turismo/reservaciones/historial-por-codigo/${codigo}`,
        { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      return r.json();
    },
    enabled: !!codigo && open,
    staleTime: 30_000,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5,
                          bgcolor: '#0f172a', color: '#fff' }}>
        <EventNoteIcon />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
            Historial de reserva
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#cbd5e1', fontFamily: 'monospace' }}>
            {codigo}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box sx={{ p: 3, textAlign: 'center', color: '#b91c1c' }}>
            {error.message || 'Error al cargar el historial'}
          </Box>
        )}
        {data && <Contenido data={data} onVoucher={setVoucherLightbox} />}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
      </DialogActions>

      {/* Lightbox de voucher — se apila sobre este modal */}
      {voucherLightbox && (
        <VoucherLightbox voucher={voucherLightbox} onClose={() => setVoucherLightbox(null)} />
      )}
    </Dialog>
  );
}

function Contenido({ data, onVoucher }) {
  const { reserva, actual, snapshot_original: snap, creacion, eventos, pagos, vouchers } = data;

  return (
    <Box sx={{ p: 2 }}>
      {/* CARD 1 · Cliente + creación */}
      <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Info icon={<PersonIcon />} label="Cliente" value={reserva.nombre_cliente} />
          <Info label="WhatsApp" value={reserva.numero_whatsapp || '—'} />
          <Info label="Correo" value={reserva.correo || '—'} />
          {actual.agencia_nombre && (
            <Info label="Agencia" value={actual.agencia_nombre} />
          )}
        </Box>
        {creacion && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Info icon={<PersonIcon />} label="Creada por"
                value={creacion.usuario_nombre || creacion.usuario_email || '(sin autor)'} />
              <Info icon={<CalendarTodayIcon />} label="Fecha creación"
                value={fmtDate(creacion.created_at)} />
              <Info icon={<DevicesIcon />} label="Desde"
                value={creacion.origen || 'desconocido'} />
            </Box>
          </>
        )}
      </Box>

      {/* CARD 2 · Snapshot original vs Estado actual */}
      <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a', mb: 1.5,
                          display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 4, height: 14, bgcolor: '#0d9488', borderRadius: 0.5 }} />
          Snapshot original vs Estado actual
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Campo</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#7c3aed' }}>Original (al crear)</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#15803d' }}>Actual</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Δ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Pasajeros originales (del desglose_precio actual como ref) */}
              {snap.pasajeros_ref && (
                <FilaComparativa label="Pasajeros"
                  original={`${snap.pasajeros_ref.adultos || 0}A · ${snap.pasajeros_ref.ninos || 0}N · ${snap.pasajeros_ref.bebes || 0}B`}
                  actual={`${actual.adultos || 0}A · ${actual.ninos || 0}N · ${actual.bebes || 0}B`} />
              )}
              <FilaComparativa label="PVP total"
                original={snap.precio_total != null ? fmt(snap.precio_total) : '—'}
                actual={fmt(actual.precio_total)} />
              <FilaComparativa label="Comisión total"
                original={snap.comision_total_al_crear != null ? fmt(snap.comision_total_al_crear) : '—'}
                actual={fmt(actual.comision_valor)} />
              <FilaComparativa label="Comisión anticipada (agencia retuvo)"
                original={snap.comision_anticipada_al_crear != null ? fmt(snap.comision_anticipada_al_crear) : '—'}
                actual={fmt(actual.comision_anticipada)} />
              <FilaComparativa label="Comisión por pagar (Entretur debe)"
                original={
                  snap.comision_total_al_crear != null && snap.comision_anticipada_al_crear != null
                    ? fmt(Math.max(snap.comision_total_al_crear - snap.comision_anticipada_al_crear, 0))
                    : '—'
                }
                actual={fmt(Math.max((actual.comision_valor || 0) - (actual.comision_anticipada || 0), 0))} />
              <FilaComparativa label="Neto Entretur esperado"
                original={snap.neto_entretur_esperado != null ? fmt(snap.neto_entretur_esperado) : '—'}
                actual={fmt((actual.precio_total || 0) - (actual.comision_valor || 0))} />
              <FilaComparativa label="Saldo pendiente"
                original={snap.saldo_inicial != null ? fmt(snap.saldo_inicial) : '—'}
                actual={fmt(actual.saldo_pendiente)} />
              <FilaComparativa label="A cargo de"
                original={snap.a_cargo_inicial || '—'}
                actual={actual.saldo_a_cargo_de || (actual.saldo_pendiente > 0 ? '?' : 'saldado')} />
              <FilaComparativa label="Forma de pago"
                original={snap.forma_pago_agencia || '—'}
                actual={actual.forma_pago_agencia || '—'} />
              <FilaComparativa label="Salida"
                original={
                  snap.paquete_nombre
                    ? `${snap.paquete_nombre}${snap.salida_fecha ? ` · ${snap.salida_fecha.slice(0,10)}` : ''}`
                    : (snap.salida_id != null ? `id=${snap.salida_id}` : '—')
                }
                actual={`${actual.paquete_nombre || '—'}${actual.salida_fecha ? ` · ${actual.salida_fecha.slice(0,10)}` : ''}`} />
              <FilaComparativa label="Agencia"
                original={snap.agencia_nombre || (snap.agencia_id != null ? `id=${snap.agencia_id}` : '—')}
                actual={actual.agencia_nombre || `id=${actual.agencia_id || '—'}`} />
              <FilaComparativa label="Tipo"
                original={snap.tipo_reservacion || '—'}
                actual={actual.tipo_reservacion || '—'} />
              <FilaComparativa label="Canal"
                original={snap.canal_reserva || '—'}
                actual={actual.canal_reserva || '—'} />
              {/* Regla de comisión aplicada al crear (informativa) */}
              {snap.comision_tipo && (
                <FilaSoloActual
                  label="Regla de comisión aplicada"
                  valor={
                    snap.comision_tipo === 'valor_fijo'
                      ? `Fija · $${new Intl.NumberFormat('es-CO').format(snap.comision_valor_adulto || 0)} adulto · $${new Intl.NumberFormat('es-CO').format(snap.comision_valor_nino || 0)} niño`
                      : snap.comision_tipo
                  }
                />
              )}
              {/* Estado del ciclo — solo actual */}
              <FilaSoloActual label="Estado" valor={actual.estado || '—'} />
              <FilaSoloActual label="Estado embarque" valor={actual.estado_embarque || '—'} />
              {actual.voucher_pendiente_subsanar && (
                <FilaSoloActual label="Voucher pendiente subsanar"
                  valor="⚠️ Sí" warn />
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 1, fontStyle: 'italic' }}>
          Los valores originales de comisión se reconstruyen a partir del pago de comisión anticipada
          generado al momento de crear y de la regla de comisión configurada para esta agencia+paquete.
          Los campos marcados como "estado del ciclo" solo aplican al estado actual (no formaban parte
          de la creación).
        </Typography>
      </Box>

      {/* CARD 3 · Timeline */}
      <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a', mb: 1.5,
                          display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 4, height: 14, bgcolor: '#7c3aed', borderRadius: 0.5 }} />
          Timeline ({eventos.length} evento{eventos.length !== 1 ? 's' : ''})
        </Typography>
        <Box sx={{ position: 'relative', pl: 2 }}>
          {/* Línea vertical */}
          <Box sx={{ position: 'absolute', left: 8, top: 8, bottom: 8,
                      width: 2, bgcolor: '#e2e8f0' }} />
          {eventos.map((ev, i) => {
            const meta = metaAccion(ev.accion);
            return (
              <Box key={ev.id || i} sx={{ position: 'relative', mb: 1.5, pl: 2 }}>
                <Box sx={{
                  position: 'absolute', left: -8, top: 2,
                  width: 16, height: 16, borderRadius: '50%',
                  bgcolor: meta.color, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 800, border: '2px solid #fff',
                }}>
                  {meta.icon}
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: meta.color }}>
                  {meta.label}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                  {fmtDate(ev.created_at)}
                  {ev.usuario_nombre && ` · ${ev.usuario_nombre}`}
                  {ev.origen && ` · ${ev.origen}`}
                </Typography>
                {ev.descripcion && (
                  <Typography sx={{ fontSize: 11, color: '#334155', mt: 0.25 }}>
                    {ev.descripcion}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* CARD 4 · Pagos */}
      {pagos.length > 0 && (
        <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a', mb: 1.5,
                            display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 4, height: 14, bgcolor: '#f59e0b', borderRadius: 0.5 }} />
            Pagos ({pagos.length})
          </Typography>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Fecha</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Monto</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Origen</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Método</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cuenta destino</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Notas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagos.map(p => (
                  <TableRow key={p.id} sx={{
                    opacity: p.revertido ? 0.5 : 1,
                    textDecoration: p.revertido ? 'line-through' : 'none',
                    bgcolor: p.es_cruce ? '#fef3c7' : 'transparent',
                  }}>
                    <TableCell sx={{ fontSize: 11 }}>{fmtDate(p.created_at)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>
                      {fmt(p.monto)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>
                      {p.origen}
                      {p.es_cruce && (
                        <Chip label="CRUCE" size="small"
                          sx={{ ml: 0.5, height: 16, fontSize: 9, fontWeight: 700,
                                bgcolor: '#a16207', color: '#fff' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{p.metodo_pago || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>
                      {p.es_cruce
                        ? <em style={{ color: '#94a3b8' }}>sin cuenta (asiento contable)</em>
                        : (p.cuenta_destino_nombre || '—')}
                    </TableCell>
                    <TableCell sx={{ fontSize: 10, color: '#64748b', maxWidth: 240, overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={
                        (p.notas || '') +
                        (p.pago_recibido_registrado_por ? ` · registrado por ${p.pago_recibido_registrado_por}` : '')
                      }>
                        <span>
                          {p.notas || '—'}
                          {p.pago_recibido_registrado_por && (
                            <Box component="span" sx={{ display: 'block', fontSize: 9,
                                                          color: '#0891b2', fontWeight: 700, mt: 0.25 }}>
                              👤 {p.pago_recibido_registrado_por}
                            </Box>
                          )}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* CARD 5 · Vouchers */}
      {vouchers.length > 0 && (
        <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a', mb: 1.5,
                            display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 4, height: 14, bgcolor: '#0891b2', borderRadius: 0.5 }} />
            Vouchers ({vouchers.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {vouchers.map(v => (
              <Box key={v.id}
                onClick={() => v.url && onVoucher(v)}
                sx={{
                  width: 80, borderRadius: 1, border: '1px solid #e2e8f0',
                  bgcolor: '#f8fafc', overflow: 'hidden',
                  cursor: v.url ? 'pointer' : 'default',
                  transition: 'transform 0.15s',
                  '&:hover': v.url ? { transform: 'scale(1.05)', borderColor: '#0d9488' } : {},
                }}>
                <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: '#f1f5f9' }}>
                  {v.url
                    ? <img src={v.url} alt="voucher" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <AttachFileIcon sx={{ fontSize: 32, color: '#94a3b8' }} />}
                </Box>
                <Box sx={{ p: 0.5 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>
                    {fmt(v.monto)}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: '#64748b' }}>
                    #{v.id} · {v.estado}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Helpers de UI ─────────────────────────────────────────────────
function Info({ icon, label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 12 } })}
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#0f172a', fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

function FilaComparativa({ label, original, actual }) {
  const cambio = String(original) !== String(actual) && original !== '—';
  return (
    <TableRow sx={{ bgcolor: cambio ? '#fef3c7' : 'transparent' }}>
      <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{label}</TableCell>
      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{original}</TableCell>
      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: cambio ? 800 : 400 }}>{actual}</TableCell>
      <TableCell align="center" sx={{ fontSize: 12 }}>
        {cambio ? <span style={{ color: '#a16207' }}>≠</span> : <span style={{ color: '#94a3b8' }}>=</span>}
      </TableCell>
    </TableRow>
  );
}

function FilaSoloActual({ label, valor, warn = false }) {
  return (
    <TableRow>
      <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{label}</TableCell>
      <TableCell sx={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>—</TableCell>
      <TableCell colSpan={2} sx={{ fontSize: 11, fontFamily: 'monospace',
                                     color: warn ? '#a16207' : '#0f172a',
                                     fontWeight: warn ? 700 : 400 }}>
        {valor}
      </TableCell>
    </TableRow>
  );
}

function VoucherLightbox({ voucher, onClose }) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 700, py: 1.5 }}>
        Voucher #{voucher.id} · {voucher.tipo || 'sin tipo'} · {fmt(voucher.monto)}
        {voucher.referencia && (
          <Box component="span" sx={{ fontSize: 12, color: '#64748b', ml: 1 }}>
            ref {voucher.referencia}
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: '#0f172a', textAlign: 'center' }}>
        <img src={voucher.url} alt="voucher"
          style={{ maxWidth: '100%', maxHeight: '75vh', display: 'block', margin: '0 auto' }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
